import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { MetaFunction } from "@remix-run/node";
import { Link, useFetcher } from "@remix-run/react";
import { useId } from "react";

import AttributionFields from "~/components/forms/AttributionFields";
import { CONTACT_CITY, CONTACT_EMAIL, CONTACT_PHONE } from "~/config/constants";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import generateSitemapEntriesFor from "~/helpers/sitemapEntriesWithLocal";
import common from "~/i18n/common";
import pages from "~/i18n/pages";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";
import { breadcrumbSchema } from "~/seo/schema";

import { contactFormAction } from "./action";
import "./contact.scss";

export const meta: MetaFunction = ({ params }) => {
  const lang = langFromParams(params);
  const t = pages[lang].contact;

  return [
    ...buildMeta({
      lang,
      path: "/contact",
      title: t.metaTitle,
      description: t.metaDescription,
    }),
    {
      "script:ld+json": breadcrumbSchema(lang, [
        { name: common[lang].a11y.home, path: "/" },
        { name: common[lang].nav.contact, path: "/contact" },
      ]),
    },
  ];
};

export const handle: SEOHandle = generateSitemapEntriesFor(
  "/contact",
  undefined,
  "monthly",
  0.8,
);

export const action = contactFormAction;

type ActionData = { success?: boolean; error?: string };

export default function ContactPage() {
  const t = useT(pages).contact;
  const c = useT(common);
  const localize = useLocalizedPath();
  const fetcher = useFetcher<ActionData>();

  const nameId = useId();
  const emailId = useId();
  const subjectId = useId();
  const messageId = useId();
  const consentId = useId();

  const busy = fetcher.state !== "idle";
  const sent = fetcher.data?.success === true;

  const details = [
    {
      icon: "☎",
      label: t.phone,
      value: CONTACT_PHONE,
      href: `tel:${CONTACT_PHONE.replace(/\s/g, "")}`,
    },
    {
      icon: "✉",
      label: t.email,
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
    },
    { icon: "◎", label: t.location, value: CONTACT_CITY, href: null },
  ];

  return (
    <main id="main-content">
      <section className="ss-container ss-contact">
        <div>
          <p className="ss-eyebrow ss-eyebrow--blue">{t.eyebrow}</p>
          <h1 className="ss-contact__title">{t.title}</h1>
          <p className="ss-contact__lead">{t.lead}</p>

          <ul className="ss-contact__details">
            {details.map((detail) => (
              <li key={detail.label} className="ss-contact__detail">
                <span className="ss-contact__icon" aria-hidden="true">
                  {detail.icon}
                </span>
                <div>
                  <div className="ss-contact__detail-label">{detail.label}</div>
                  <div className="ss-contact__detail-value">
                    {detail.href ? (
                      <a href={detail.href}>{detail.value}</a>
                    ) : (
                      detail.value
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="ss-contact__card">
          <h2 className="ss-contact__form-title">{t.formTitle}</h2>

          {sent ? (
            <div className="ss-contact__success" role="status">
              <span className="ss-contact__check" aria-hidden="true">
                ✓
              </span>
              <h3>{t.successTitle}</h3>
              <p>{t.successBody}</p>
            </div>
          ) : (
            <fetcher.Form method="post" className="ss-contact__form">
              <div className="ss-field">
                <label className="ss-field__label" htmlFor={nameId}>
                  {t.fullName}*
                </label>
                <input
                  id={nameId}
                  name="fullName"
                  className="ss-field__input"
                  required
                  autoComplete="name"
                  placeholder={t.fullNamePlaceholder}
                />
              </div>

              <div className="ss-field">
                <label className="ss-field__label" htmlFor={emailId}>
                  {t.emailLabel}*
                </label>
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  className="ss-field__input"
                  required
                  autoComplete="email"
                  placeholder={t.emailPlaceholder}
                />
              </div>

              <div className="ss-field">
                <label className="ss-field__label" htmlFor={subjectId}>
                  {t.subject}*
                </label>
                <input
                  id={subjectId}
                  name="subject"
                  className="ss-field__input"
                  required
                  placeholder={t.subjectPlaceholder}
                />
              </div>

              <div className="ss-field">
                <label className="ss-field__label" htmlFor={messageId}>
                  {t.message}*
                </label>
                <textarea
                  id={messageId}
                  name="message"
                  className="ss-field__textarea"
                  rows={4}
                  required
                  placeholder={t.messagePlaceholder}
                />
              </div>

              <AttributionFields />

              <div className="ss-honeypot" aria-hidden="true">
                <label htmlFor={`${nameId}-address`}>Address</label>
                <input
                  id={`${nameId}-address`}
                  name="address"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <label className="ss-consent" htmlFor={consentId}>
                <input
                  id={consentId}
                  type="checkbox"
                  name="consent"
                  value="yes"
                  required
                />
                <span>
                  {c.form.consentLabel}{" "}
                  <Link
                    to={localize("/privacy")}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {c.form.consentLink}
                  </Link>{" "}
                  {c.form.consentSuffix}
                </span>
              </label>

              {fetcher.data?.error ? (
                <p
                  className="ss-form-status ss-form-status--error"
                  role="alert"
                >
                  {fetcher.data.error === "RATE_LIMITED"
                    ? c.form.rateLimited
                    : t.error}
                </p>
              ) : null}

              <button
                type="submit"
                className="ss-btn ss-btn--primary ss-btn--block"
                disabled={busy}
                aria-disabled={busy}
              >
                {busy ? t.sending : t.send}
              </button>
            </fetcher.Form>
          )}
        </div>
      </section>
    </main>
  );
}
